export function validateGroupedCombination(instrument) {
  const resolver = instrument?.result_key_resolver || {};
  if (resolver.mode !== "grouped_combination") return [];

  const errors = [];
  const dimensions = instrument?.dimensions || [];
  const byId = new Map(dimensions.map((dimension) => [dimension.id, dimension]));
  const codeCounts = new Map();
  dimensions.forEach((dimension) => {
    if (dimension.code) codeCounts.set(dimension.code, (codeCounts.get(dimension.code) || 0) + 1);
  });
  codeCounts.forEach((count, code) => {
    if (count > 1) errors.push(`Duplicate dimension code: ${code}`);
  });

  const criteriaByDimension = new Map(
    (instrument?.scoring?.criteria || []).map((criterion) => [criterion.dimension_id, criterion])
  );
  const groupings = resolver.dimension_groupings || [];
  if (!groupings.length) errors.push("Select at least one dimension and configure its groups.");

  const groupingByCode = new Map();
  groupings.forEach((grouping) => {
    const dimension = byId.get(grouping.dimension_id);
    if (!dimension) {
      errors.push(`Missing selected dimension: ${grouping.dimension_code || grouping.dimension_id || "empty"}`);
      return;
    }
    if (!dimension.code) errors.push(`Selected dimension ${dimension.name_en || dimension.id} needs a stable code.`);
    if (groupingByCode.has(dimension.code)) errors.push(`Duplicate grouping dimension: ${dimension.code}`);
    groupingByCode.set(dimension.code, grouping);

    const groups = grouping.groups || [];
    if (!groups.length) errors.push(`Groups are required for ${dimension.code}.`);
    const groupCodes = groups.map((group) => group.code).filter(Boolean);
    if (groupCodes.length !== groups.length) errors.push(`Empty group code in ${dimension.code}.`);
    if (new Set(groupCodes).size !== groupCodes.length) errors.push(`Duplicate group code in ${dimension.code}.`);

    const assignments = new Map();
    groups.forEach((group) => (group.source_categories || []).forEach((category) => {
      assignments.set(category, [...(assignments.get(category) || []), group.code]);
    }));
    assignments.forEach((assigned, category) => {
      if (assigned.length > 1) errors.push(`Category ${category} belongs to multiple groups in ${dimension.code}.`);
    });
    const criterion = criteriaByDimension.get(dimension.id);
    const ranges = criterion?.ranges || [];
    if (!criterion || !ranges.length) errors.push(`Scoring criteria are required for ${dimension.code}.`);
    if (ranges.some((range) => !range.code)) errors.push(`Every score range in ${dimension.code} needs a stable category code.`);
    const categories = ranges.map((range) => range.code).filter(Boolean);
    categories.forEach((category) => {
      if (!assignments.has(category)) errors.push(`Category ${category} is not assigned in ${dimension.code}.`);
    });
  });

  const configuredCodes = new Set(groupingByCode.keys());
  const seenRules = new Set();
  const coveredRules = new Set();
  (resolver.combination_rules || []).forEach((rule) => {
    const when = rule.when || {};
    const signature = JSON.stringify(Object.entries(when).sort());
    if (seenRules.has(signature)) errors.push("Duplicate combination rule.");
    seenRules.add(signature);
    if (!String(rule.result_key || "").trim()) errors.push("Every combination rule needs a result key.");
    Object.entries(when).forEach(([dimensionCode, groupCode]) => {
      if (!configuredCodes.has(dimensionCode)) errors.push(`Rule references unknown dimension: ${dimensionCode}.`);
      const knownGroups = new Set((groupingByCode.get(dimensionCode)?.groups || []).map((group) => group.code));
      if (configuredCodes.has(dimensionCode) && !knownGroups.has(groupCode)) {
        errors.push(`Rule references unknown group ${groupCode} for ${dimensionCode}.`);
      }
    });
    if (Object.keys(when).length === configuredCodes.size && Object.keys(when).every((code) => configuredCodes.has(code))) {
      coveredRules.add(signature);
    }
  });

  const combinations = cartesian(
    [...groupingByCode.entries()].map(([dimensionCode, grouping]) =>
      (grouping.groups || []).filter((group) => group.code).map((group) => [dimensionCode, group.code])
    )
  );
  if (combinations.some((combination) => !coveredRules.has(JSON.stringify(combination.sort())))) {
    errors.push("Combination rules must cover every configured group combination.");
  }
  return [...new Set(errors)];
}

export function buildCombinationRows(groupings) {
  return cartesian((groupings || []).map((grouping) =>
    (grouping.groups || []).filter((group) => group.code).map((group) => [grouping.dimension_code, group.code])
  )).map((entries) => ({ when: Object.fromEntries(entries), result_key: "" }));
}

export function renameGroupedDimension(resolver, dimensionId, previousCode, nextCode) {
  if (resolver?.mode !== "grouped_combination" || previousCode === nextCode) return resolver;
  return {
    ...resolver,
    dimension_groupings: (resolver.dimension_groupings || []).map((grouping) =>
      grouping.dimension_id === dimensionId ? { ...grouping, dimension_code: nextCode } : grouping
    ),
    combination_rules: (resolver.combination_rules || []).map((rule) => {
      if (!Object.prototype.hasOwnProperty.call(rule.when || {}, previousCode)) return rule;
      const when = { ...(rule.when || {}) };
      const group = when[previousCode];
      delete when[previousCode];
      if (nextCode) when[nextCode] = group;
      return { ...rule, when };
    }),
  };
}

export function removeGroupedDimension(resolver, dimensionId) {
  if (resolver?.mode !== "grouped_combination") return resolver;
  return {
    ...resolver,
    dimension_groupings: (resolver.dimension_groupings || []).filter((grouping) => grouping.dimension_id !== dimensionId),
    combination_rules: [],
  };
}

function cartesian(arrays) {
  if (!arrays.length || arrays.some((items) => !items.length)) return [];
  return arrays.reduce((rows, items) => rows.flatMap((row) => items.map((item) => [...row, item])), [[]]);
}
